import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Grid, Stack, TextField, Typography, Card, InputAdornment, IconButton, List, ListItem } from '@mui/material';
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
// icons
import EastIcon from '@mui/icons-material/East';
import { RemoveRedEyeRounded } from '@mui/icons-material';
import AngleRightIcon from "common/customIcons/angle_right";
import SearchIcon from 'common/customIcons/search';
// utils and helpers
import { formatTimestampToWithoutTime, formatTimeStampToDate } from "utilities/common";
import { debounce } from "utilities/common";
import { getRegionClosureForms } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from "components/MDButton";
import NoDataFound from "common/component/NoDataFound";
import { defaultColDef } from "components/tables/CommonAgGridTable";
import StatusText from "common/component/StatusText";
import { StatusFilter } from "common/filters";
import ClosureNotes from 'common/CommonModal/Hyperwolf/cosureNotes';
import { TableLoader } from "components/tables/AgGridAccordionChildScrollingTable";

const statusOptions = [
  { value: "", label: "All" },
  { value: "Pending from Driver", label: "Pending from Driver" },
  { value: "Closed", label: "Closed" },
  { value: "Closure Pending", label: "Closure Pending" },
]

const RegionClosureForms = () => {
  const gridRef = useRef();
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { distributionId } = useParams()
  const [searchParams] = useSearchParams()
  const { region, date } = Object.fromEntries(searchParams.entries())

  const [allDrivers, setAllDrivers] = useState([]);
  const [details, setDetails] = useState({});
  const [inputFilters, setInputFilters] = useState({});
  const [customFilters, setCustomFilters] = useState({});
  const [showClosureNotes, setShowClosureNotes] = useState(null);

  const columnDefs = [
    {
      headerName: 'Driver Name',
      field: 'driverName',
      suppressMenu: false,
      flex: 2,
      minWidth: 220,
      sortable: false,
    },
    {
      headerName: 'Sub Region',
      field: 'subRegion',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 180,
      sortable: false,
    },
    {
      headerName: 'Kit ID',
      field: 'kitId',
      suppressMenu: false,
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      headerName: 'Closure',
      field: 'closure',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 170,
      sortable: false,
    },
    {
      headerName: 'Total Qty',
      field: 'totalQty',
      suppressMenu: false,
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      headerName: 'Expected Qty',
      field: 'expectedQty',
      suppressMenu: false,
      flex: 1,
      minWidth: 140,
      sortable: false,
    },
    {
      headerName: 'Sold Qty',
      field: 'soldQty',
      suppressMenu: false,
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    // {
    //   headerName: 'Total Retail Value',
    //   field: 'totalInventoryValue',
    //   suppressMenu: false,
    //   flex: 1.25,
    //   minWidth: 150,
    //   sortable: false,
    //   valueGetter: ({ data }) => data?.totalInventoryValue ? `$${data?.totalInventoryValue?.toLocaleString()}` : "-",
    // },
    {
      headerName: "Today's Revenue",
      field: 'todaysRevenue',
      suppressMenu: false,
      flex: 1.25,
      minWidth: 150,
      sortable: false,
      valueGetter: ({ data }) => data?.todaysRevenue ? `$${data?.todaysRevenue?.toLocaleString()}` : "-",
    },
    {
      headerName: 'Status',
      field: 'status',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 220,
      sortable: false,
      cellRenderer: ({ data }) => <StatusText status={data?.status} />
    },
    {
      headerName: 'Closure Notes',
      field: 'closureNotes',
      suppressMenu: false,
      flex: 1,
      minWidth: 150,
      sortable: false,
      cellStyle: { textAlign: 'left', justifyContent: 'start' },
      cellRenderer: ({ data }) => {
        const { closureNotes } = data || {}
        if (!closureNotes) return "-";

        return <Box>
          <MDButton
            onClick={() => setShowClosureNotes(data)}
            size='small'
            startIcon={<RemoveRedEyeRounded fontSize='small' />}
            variant='contained'
            color='primary'
            className='btn-primary-light'
            style={{ whiteSpace: "nowrap" }}
          >
            View Notes
          </MDButton>
        </Box>
      },
    },
    {
      headerName: '',
      field: 'actions',
      suppressMenu: false,
      flex: 1,
      minWidth: 80,
      sortable: false,
      cellStyle: { textAlign: 'left', justifyContent: 'start' },
      headerClass: 'right-aligned-cell-header',
      cellRenderer: ({ data }) => {
        const { driverId, subRegionId } = data || {}
        if (!driverId) return null;

        return <Box display="flex" flexDirection="row" justifyContent="end" gap={0.5}>
          <IconButton onClick={() => navigate(`/hyperwolf/closure-forms/details/${distributionId}?driver=${driverId}&region=${region}&subRegion=${subRegionId}&date=${date}`)} size='small'>
            <EastIcon width='16px' height='16px' />
          </IconButton>
        </Box>
      },
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomFilters(prev => ({ ...prev, [name]: value }));
  }

  const handleDrivers = async () => {
    try {
      gridRef.current?.api?.showLoadingOverlay();

      const request = { distributionId, regionId: region };
      const formattedDate = formatTimeStampToDate(Number(date));
      if (formattedDate !== "Invalid date") {
        request.date = formattedDate;
      }
      const response = await dispatch(getRegionClosureForms(request)).unwrap();
      const { data } = response || {};
      if (data) {
        setDetails(data || {});
        setAllDrivers(data?.drivers || []);
      }

      if (!data?.drivers?.length) {
        gridRef.current?.api?.showNoRowsOverlay();
      } else {
        gridRef.current?.api?.hideOverlay();
      }
    } catch (error) {
      showToastMsg(error, "error");
    }
  };

  const handleDebounceSearch = useCallback(debounce((event) => {
    const { name, value } = event.target;

    setCustomFilters(prev => ({ ...prev, [name]: value }));
  }, 1000), []);

  const handleClearFilters = () => {
    setInputFilters({ search: "" });
    setCustomFilters({ search: "" });
  }

  const isExternalFilterPresent = useCallback(() => {
    return Object.values(customFilters).some(Boolean);
  }, [customFilters]);

  const doesExternalFilterPass = useCallback((node) => {
    const { status, search } = customFilters;
    const data = node.data;

    if (!data) return false;

    if (status) {
      const statusText = data.status;
      if (statusText !== status) return false;
    }

    if (search) {
      const searchValue = search.toLowerCase();
      const driverName = data.driverName?.toLowerCase();
      if (!driverName.includes(searchValue)) return false;
    }

    return true;
  }, [customFilters]);

  useEffect(() => {
    if (distributionId && region) {
      handleDrivers()
    }
  }, [distributionId, region])

  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [customFilters]);


  return (
    <CommonLayout>
      <Box p={4} className="cus-contents">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Closure Overview
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2.5, boxShadow: "none" }}>
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2}>
            {/* Distribution details */}
            <Grid item xs={12} sm={8}>
              <Typography variant='h4' fontWeight="semibold" style={{ fontSize: "20px" }}>Closure Forms</Typography>
              <List sx={{ display: "flex", gap: 3, mt: 0.5 }}>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Region:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details.region}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Date:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{formatTimestampToWithoutTime(details?.createdDate)}</Typography>
                </ListItem>
              </List>
            </Grid>

            {/* Search filter */}
            <Grid item xs={12} sm={4}>
              <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
                <Box className="search-gray">
                  <TextField
                    className="custom-search-field"
                    placeholder="Search"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">
                        <IconButton size='small'>
                          <SearchIcon width='18px' height='18px' />
                        </IconButton>
                      </InputAdornment>
                    }}
                    name="search"
                    value={inputFilters.search}
                    onChange={event => {
                      const { value } = event.target;
                      setInputFilters({ ...inputFilters, search: value })
                      handleDebounceSearch(event)
                    }}
                    style={{ minWidth: "300px" }}
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        {/* Filters */}
        <Box className='cus-filter-sec' mb={2}>
          <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
            <Grid item xs={12} lg={3} xxxl={2}>
              <StatusFilter
                value={customFilters?.status}
                handleChange={handleChange}
                placeholder="Status"
                options={statusOptions}
              />
            </Grid>
            <Grid item xs={12} lg={3} xxxl={2}>
              <MDButton
                sx={{ minWidth: "120px", fontWeight: "normal" }}
                type="button"
                variant="outlined"
                color="dark"
                disabled={!Object.values(customFilters || {})?.filter(Boolean)?.length}
                onClick={handleClearFilters}
              >
                Clear All
              </MDButton>
            </Grid>
          </Grid>
        </Box>

        {/* Drivers listing */}
        <Card sx={{ p: 0, borderRadius: 2.5, boxShadow: "none" }} className="header-ag-grid-lines table-nodata-size">
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
            <Grid item xs={12}>
              <div
                className="ag-theme-quartz"
                style={{ height: "70vh", width: "100%" }}
              >
                <AgGridReact
                  ref={gridRef}
                  className="ag-grid--table"
                  pagination={false}
                  rowData={allDrivers?.length ? allDrivers : null}
                  noRowsOverlayComponent={NoDataFound}
                  loadingOverlayComponent={TableLoader}
                  columnDefs={columnDefs}
                  rowModelType={"clientSide"}
                  headerHeight={36}
                  rowHeight={56}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  defaultColDef={{ ...defaultColDef }}
                  isExternalFilterPresent={isExternalFilterPresent}
                  doesExternalFilterPass={doesExternalFilterPass}
                  onFilterChanged={() => {
                    if (gridRef.current?.api?.getDisplayedRowCount() === 0) {
                      gridRef.current?.api?.showNoRowsOverlay();
                    } else {
                      gridRef.current?.api?.hideOverlay();
                    }
                  }}
                  onCellClicked={(props) => {
                    const { driverId, subRegionId } = props?.data || {}
                    if (!driverId || !subRegionId) return;

                    // Prevent row click if click originated from the actions column
                    if (props?.column && (["actions", "closureNotes"].includes(props.column.getColId()))) {
                      return;
                    }
                    navigate(`/hyperwolf/closure-forms/details/${distributionId}?driver=${driverId}&region=${region}&subRegion=${subRegionId}&date=${date}`)
                  }}
                />
              </div>
            </Grid>
          </Grid>
        </Card>

        {showClosureNotes && <ClosureNotes showClosureNotes={showClosureNotes} setShowClosureNotes={setShowClosureNotes} />}

      </Box>
    </CommonLayout>
  )
}

export default RegionClosureForms
